import React, { useState, useMemo } from 'react';
import { format, isToday, isThisYear } from 'date-fns';
import { flagMessage, markRead, deleteMessage, getConversation } from '../api';
import { colorByName } from '../categories';

function formatDate(dateStr) {
  const d = new Date(dateStr);
  if (isToday(d)) return format(d, 'h:mm a');
  if (isThisYear(d)) return format(d, 'MMM d');
  return format(d, 'dd/MM/yyyy');
}

function Avatar({ name, email }) {
  const initials = name
    ? name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()
    : (email || '?')[0].toUpperCase();
  const hue = [...(name || email || '')].reduce((h, c) => h + c.charCodeAt(0), 0) % 360;
  return (
    <div className="avatar" style={{ background: `hsl(${hue},45%,48%)` }}>
      {initials}
    </div>
  );
}

function ImportanceDot({ importance }) {
  if (!importance || importance === 'normal') return null;
  return <span className={`importance-dot importance-${importance}`}>{importance === 'high' ? '!' : '↓'}</span>;
}

// Groups messages by conversationId, returns array of { key, messages, latest }
function groupByConversation(messages) {
  const map = new Map();
  for (const msg of messages) {
    const key = msg.conversationId || msg.id;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(msg);
  }
  // Sort each group oldest→newest; return newest-first by latest message
  return [...map.entries()]
    .map(([key, msgs]) => {
      const sorted = [...msgs].sort((a, b) => new Date(a.receivedDateTime) - new Date(b.receivedDateTime));
      return { key, messages: sorted, latest: sorted[sorted.length - 1] };
    })
    .sort((a, b) => new Date(b.latest.receivedDateTime) - new Date(a.latest.receivedDateTime));
}

function ConversationRow({ group, selectedId, onSelect, categoryMap, onMessageUpdate, conversationView, pinnedIds = [], onTogglePin, pinned: isPinnedSection }) {
  const [expanded, setExpanded] = useState(false);
  const [threadMessages, setThreadMessages] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);

  const { latest, messages } = group;
  const count = messages.length;
  const hasMultiple = count > 1;
  const isFlagged = latest.flag?.flagStatus === 'flagged';
  const isComplete = latest.flag?.flagStatus === 'complete';
  const anyUnread = messages.some(m => !m.isRead);
  const sender = latest.from?.emailAddress;

  // Collect unique senders for thread display
  const senderNames = [...new Set(messages.map(m => m.from?.emailAddress?.name || m.from?.emailAddress?.address || '?'))];

  async function handleExpand(e) {
    e.stopPropagation();
    if (!expanded && threadMessages === null && hasMultiple) {
      setLoadingThread(true);
      try {
        const msgs = await getConversation(latest.conversationId);
        setThreadMessages(msgs);
      } catch {
        setThreadMessages(messages); // fallback to what we have
      }
      setLoadingThread(false);
    }
    setExpanded(v => !v);
  }

  async function handleFlag(e, msg) {
    e.stopPropagation();
    const newStatus = msg.flag?.flagStatus === 'flagged' ? 'notFlagged' : 'flagged';
    try { await flagMessage(msg.id, newStatus); onMessageUpdate(msg.id, { flag: { flagStatus: newStatus } }); } catch {}
  }

  async function handleMarkRead(e, msg) {
    e.stopPropagation();
    try { await markRead(msg.id, !msg.isRead); onMessageUpdate(msg.id, { isRead: !msg.isRead }); } catch {}
  }

  async function handleDelete(e, msg) {
    e.stopPropagation();
    try { await deleteMessage(msg.id); onMessageUpdate(msg.id, null); } catch {}
  }

  const isSelected = selectedId === latest.id || (threadMessages || messages).some(m => m.id === selectedId);

  return (
    <>
      {/* Thread header row */}
      <div
        className={`email-item ${isSelected && !expanded ? 'active' : ''} ${anyUnread ? 'unread' : ''}`}
        onClick={() => conversationView && hasMultiple ? handleExpand({stopPropagation: () => {}}) : onSelect(latest)}
      >
        <div className="email-item-left">
          <Avatar name={sender?.name} email={sender?.address} />
          {anyUnread && <span className="unread-dot" />}
        </div>

        <div className="email-item-body">
          <div className="email-item-row1">
            <span className="email-from">
              <ImportanceDot importance={latest.importance} />
              {conversationView && hasMultiple
                ? senderNames.slice(0, 3).join(', ')
                : (sender?.name || sender?.address || '(no sender)')}
            </span>
            <span className="email-date">{formatDate(latest.receivedDateTime)}</span>
          </div>

          <div className="email-item-row2">
            <span className="email-subject">{latest.subject || '(no subject)'}</span>
            <div className="email-item-actions">
              {latest.hasAttachments && <span className="icon-sm" title="Attachment">📎</span>}
              {conversationView && hasMultiple && (
                <span className="thread-count" onClick={handleExpand}>
                  {loadingThread ? '…' : expanded ? '▴' : `▾ ${count}`}
                </span>
              )}
              <span
                className={`flag-btn ${isFlagged ? 'flagged' : ''} ${isComplete ? 'complete' : ''}`}
                onClick={e => handleFlag(e, latest)}
              >
                {isComplete ? '✅' : '🚩'}
              </span>
            </div>
          </div>

          <div className="email-item-row3">
            <span className="email-preview">{latest.bodyPreview}</span>
          </div>

          {latest.categories?.length > 0 && (
            <div className="email-categories">
              {latest.categories.map(cat => (
                <span key={cat} className="category-chip" style={{ background: categoryMap?.[cat] || colorByName(cat) }}>{cat}</span>
              ))}
            </div>
          )}
        </div>

        <div className="email-item-hover-actions">
          <button title="Delete" onClick={e => handleDelete(e, latest)}>🗑</button>
          <button title={latest.isRead ? 'Mark unread' : 'Mark read'} onClick={e => handleMarkRead(e, latest)}>
            {latest.isRead ? '📩' : '📨'}
          </button>
          {onTogglePin && (
            <button
              title={pinnedIds.includes(latest.id) ? 'Unpin' : 'Pin'}
              onClick={e => { e.stopPropagation(); onTogglePin(latest.id); }}
            >
              {pinnedIds.includes(latest.id) ? '📌' : '📍'}
            </button>
          )}
        </div>
      </div>

      {/* Expanded thread messages */}
      {expanded && (threadMessages || messages).map((msg, i) => {
        const msgSender = msg.from?.emailAddress;
        const msgFlagged = msg.flag?.flagStatus === 'flagged';
        return (
          <div
            key={msg.id}
            className={`email-item thread-item ${selectedId === msg.id ? 'active' : ''} ${!msg.isRead ? 'unread' : ''}`}
            onClick={() => onSelect(msg)}
          >
            <div className="thread-indent">
              <div className="thread-line" style={{ opacity: i < (threadMessages || messages).length - 1 ? 1 : 0 }} />
              <Avatar name={msgSender?.name} email={msgSender?.address} />
              {!msg.isRead && <span className="unread-dot" />}
            </div>

            <div className="email-item-body">
              <div className="email-item-row1">
                <span className="email-from">{msgSender?.name || msgSender?.address || '(no sender)'}</span>
                <span className="email-date">{formatDate(msg.receivedDateTime)}</span>
              </div>
              <div className="email-item-row3">
                <span className="email-preview">{msg.bodyPreview}</span>
              </div>
              {msg.categories?.length > 0 && (
                <div className="email-categories">
                  {msg.categories.map(cat => (
                    <span key={cat} className="category-chip" style={{ background: categoryMap?.[cat] || colorByName(cat) }}>{cat}</span>
                  ))}
                </div>
              )}
            </div>

            <div className="email-item-hover-actions">
              <button onClick={e => handleDelete(e, msg)}>🗑</button>
              <span className={`flag-btn ${msgFlagged ? 'flagged' : ''}`} onClick={e => handleFlag(e, msg)}>🚩</span>
            </div>
          </div>
        );
      })}
    </>
  );
}

export default function EmailList({
  messages, selectedId, onSelect, onSearch, onFilterChange,
  filter, loading, onLoadMore, hasMore, categoryMap, onMessageUpdate,
  pinnedIds = [], onTogglePin, folderName
}) {
  const [searchVal, setSearchVal] = useState('');
  const [conversationView, setConversationView] = useState(true);

  const { pinned, rest } = useMemo(() => {
    const pinnedSet = new Set(pinnedIds);
    return {
      pinned: messages.filter(m => pinnedSet.has(m.id)),
      rest: messages.filter(m => !pinnedSet.has(m.id)),
    };
  }, [messages, pinnedIds]);

  const pinnedGroups = useMemo(() =>
    conversationView ? groupByConversation(pinned) : pinned.map(m => ({ key: m.id, messages: [m], latest: m })),
    [pinned, conversationView]
  );

  const groups = useMemo(() =>
    conversationView ? groupByConversation(rest) : rest.map(m => ({ key: m.id, messages: [m], latest: m })),
    [rest, conversationView]
  );

  function handleSearch(e) {
    e.preventDefault();
    onSearch(searchVal);
  }

  return (
    <div className="email-list-panel">
      {folderName && <div className="folder-name-header">{folderName}</div>}
      <div className="list-toolbar">
        <form className="search-bar" onSubmit={handleSearch}>
          <span className="search-icon">🔍</span>
          <input
            value={searchVal}
            onChange={e => setSearchVal(e.target.value)}
            placeholder="Search"
          />
          {searchVal && (
            <button type="button" className="search-clear" onClick={() => { setSearchVal(''); onSearch(''); }}>✕</button>
          )}
        </form>
        <div className="filter-row">
          <div className="filter-tabs">
            {['all', 'unread', 'flagged'].map(f => (
              <button key={f} className={`filter-tab ${filter === f ? 'active' : ''}`} onClick={() => onFilterChange(f)}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <button
            className={`conv-toggle ${conversationView ? 'active' : ''}`}
            onClick={() => setConversationView(v => !v)}
            title={conversationView ? 'Switch to message view' : 'Switch to conversation view'}
          >
            💬 {conversationView ? 'Conversations' : 'Messages'}
          </button>
        </div>
      </div>

      <div className="email-list">
        {loading && messages.length === 0 && <div className="list-status">Loading…</div>}
        {!loading && messages.length === 0 && <div className="list-status">No messages</div>}

        {pinnedGroups.length > 0 && (
          <>
            <div className="list-section-header">📌 Pinned</div>
            {pinnedGroups.map(group => (
              <ConversationRow
                key={group.key}
                group={group}
                selectedId={selectedId}
                onSelect={onSelect}
                categoryMap={categoryMap}
                onMessageUpdate={onMessageUpdate}
                conversationView={conversationView}
                pinnedIds={pinnedIds}
                onTogglePin={onTogglePin}
                pinned
              />
            ))}
            <div className="list-section-header">All messages</div>
          </>
        )}

        {groups.map(group => (
          <ConversationRow
            key={group.key}
            group={group}
            selectedId={selectedId}
            onSelect={onSelect}
            categoryMap={categoryMap}
            onMessageUpdate={onMessageUpdate}
            conversationView={conversationView}
            pinnedIds={pinnedIds}
            onTogglePin={onTogglePin}
          />
        ))}

        {hasMore && (
          <button className="load-more-btn" onClick={onLoadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
}
